import { vehiclePrices } from '../vehiclePrices';
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import m6HeroV2L from '../images/vtol-pc.webp';
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from '../PageSeo';

function M6Detail() {
  const priceStandard = vehiclePrices.m6[0].priceValue;
  const priceSuperiorCaptain = vehiclePrices.m6[2].priceValue;
  const formattedStartingPrice = vehiclePrices.m6[0].price;

  const pageTitle = `BYD M6 Indonesia | Harga ${formattedStartingPrice} MPV Listrik`;
  const pageDescription = `Cek harga BYD M6 mulai ${formattedStartingPrice} di BYD BSD Serpong. MPV listrik 6-7 seater untuk keluarga, fitur V2L, jarak tempuh hingga 530 km, dan test drive.`;

  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <PageSeo
        title={pageTitle}
        description={pageDescription}
        keywords={[
          ...keywordSets.localDealer,
          'BYD M6 Indonesia',
          'harga BYD M6',
          'MPV listrik keluarga',
          'BYD M6 Captain Seat',
          'BYD M6 BSD Serpong',
        ]}
        path='/blog/byd-m6-mpv-listrik-keluarga'
        image={m6HeroV2L}
        type='article'
        jsonLd={[
          productJsonLd({
            name: 'BYD M6',
            description: pageDescription,
            image: m6HeroV2L,
            path: '/blog/byd-m6-mpv-listrik-keluarga',
            lowPrice: priceStandard,
            highPrice: priceSuperiorCaptain,
            offerCount: 3,
          }),
          articleJsonLd({
            title: pageTitle,
            description: pageDescription,
            image: m6HeroV2L,
            path: '/blog/byd-m6-mpv-listrik-keluarga',
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: 'BYD M6', path: '/blog/byd-m6-mpv-listrik-keluarga' },
          ]),
        ]}
      />

      <Box sx={{ mt: 10 }}>
        {/* H1: Judul Utama SEO yang Kuat */}
        <Typography
          variant='h3'
          component='h1'
          fontWeight='bold'
          gutterBottom
          sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
        >
          BYD M6: MPV Listrik Keluarga Pertama di Indonesia dengan Fitur V2L
        </Typography>

        {/* Satu-Satunya Gambar Hero (V2L Context) */}
        <Box
          component='img'
          src={m6HeroV2L}
          alt='Keluarga Indonesia menikmati fitur V2L BYD M6 untuk camping, mengisi daya laptop dan alat kopi di luar ruangan'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 2,
            mb: 4,
            boxShadow: 3,
          }}
        />

        {/* Paragraf Pembuka dengan Kata Kunci LSI */}
        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          <strong>BYD M6</strong> resmi hadir sebagai pionir{' '}
          <strong>MPV Listrik Keluarga</strong> di pasar otomotif Indonesia.
          Mobil ini dirancang khusus untuk memenuhi kebutuhan mobilitas keluarga
          modern yang menginginkan kendaraan luas, nyaman, dan ramah lingkungan.
          Keunggulan utamanya, seperti terlihat pada gambar di atas, adalah
          fitur <strong>Vehicle-to-Load (V2L)</strong> yang memungkinkan BYD M6
          menjadi sumber listrik berjalan untuk aktivitas outdoor. Selain
          kenyamanan, pemilik BYD M6 di Jabodetabek menikmati fasilitas{' '}
          <strong>Bebas Ganjil Genap</strong> di Jakarta serta biaya{' '}
          <strong>Pajak Kendaraan Listrik Murah</strong> yang sangat efisien
          dibanding MPV bensin konvensional.
        </Typography>

        {/* H2: Varian & Harga - Sangat Bagus untuk SEO Comparison */}
        <Typography
          variant='h4'
          component='h2'
          fontWeight='bold'
          sx={{ mt: 5, mb: 3 }}
        >
          Daftar Harga & Spesifikasi 3 Varian BYD M6
        </Typography>

        <TableContainer component={Paper} elevation={3} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#1976d2' }}>
              <TableRow>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Fitur Utama
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Standard (7-Seater)
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Superior (7-Seater)
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Superior Captain (6-Seater)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Harga OTR Jakarta</strong>
                </TableCell>
                <TableCell>
                  <strong>Rp395.000.000</strong>
                </TableCell>
                <TableCell>
                  <strong>Rp435.000.000</strong>
                </TableCell>
                <TableCell>
                  <strong>Rp445.000.000</strong>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Kapasitas Baterai</TableCell>
                <TableCell>55.4 kWh</TableCell>
                <TableCell colSpan={2} align='center'>
                  71.8 kWh
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Jarak Tempuh (NEDC)</TableCell>
                <TableCell>420 km</TableCell>
                <TableCell colSpan={2} align='center'>
                  530 km
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Daya Maksimum</TableCell>
                <TableCell>120 kW</TableCell>
                <TableCell colSpan={2} align='center'>
                  150 kW
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Torsi Maksimum</TableCell>
                <TableCell colSpan={3} align='center'>
                  310 N.m
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Akselerasi (0-100 km/h)</TableCell>
                <TableCell>10.1 detik</TableCell>
                <TableCell colSpan={2} align='center'>
                  8.6 detik
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Konfigurasi Kursi</TableCell>
                <TableCell colSpan={2} align='center'>
                  7 Penumpang (Bench)
                </TableCell>
                <TableCell>6 Penumpang (Captain Seat)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* H2: Konten Padat Teks - Keunggulan Utama */}
        <Typography
          variant='h4'
          component='h2'
          fontWeight='bold'
          sx={{ mt: 5, mb: 3 }}
        >
          Mengapa BYD M6 Adalah Pilihan Tepat untuk Keluarga Indonesia?
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant='h6' fontWeight='bold' gutterBottom>
              1. Kenyamanan First Class & Fleksibilitas Kabin
            </Typography>
            <Typography variant='body1' paragraph>
              BYD M6 menawarkan dua pilihan konfigurasi kursi yang fleksibel.
              Varian <strong>7-seater</strong> (Standard & Superior) sangat
              ideal untuk keluarga besar, sementara varian{' '}
              <strong>Superior Captain (6-seater)</strong> memberikan kenyamanan
              ala jet pribadi dengan kursi independen di baris kedua, lengkap
              dengan sandaran tangan dan akses mudah ke baris ketiga. Material
              soft-touch dan panoramic sunroof (pada varian Superior) semakin
              menambah kesan mewah di dalam kabin.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant='h6' fontWeight='bold' gutterBottom>
              2. Performa Bertenaga & Jarak Tempuh Jauh
            </Typography>
            <Typography variant='body1' paragraph>
              Dibangun di atas e-Platform 3.0 yang canggih, BYD M6 didukung oleh{' '}
              <strong>BYD Blade Battery</strong> yang terkenal aman dan tahan
              lama. Dengan kapasitas baterai hingga 71.8 kWh, mobil ini mampu
              menempuh jarak hingga 530 km (NEDC) dalam sekali pengisian daya
              penuh. Torsi instan 310 N.m memberikan akselerasi yang responsif,
              sangat nyaman untuk mendahului di jalan tol maupun tanjakan.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='h6' fontWeight='bold' gutterBottom>
              3. Fitur V2L (Vehicle-to-Load): Listrik Berjalan untuk Gaya Hidup
              Aktif
            </Typography>
            <Typography variant='body1' paragraph>
              Inilah fitur yang paling dicintai oleh keluarga yang suka
              berpetualang. Seperti terlihat pada gambar utama, fitur V2L
              memungkinkan Anda menyolokkan perangkat elektronik standar
              (laptop, alat kopi, TV, lampu camping) langsung ke mobil. BYD M6
              berubah menjadi power bank raksasa yang mendukung aktivitas gaya
              hidup aktif keluarga Anda di mana saja tanpa perlu khawatir
              kehabisan daya.
            </Typography>
          </Grid>
        </Grid>

        {/* Local SEO & Aftersales */}
        <Typography
          variant='h5'
          component='h3'
          fontWeight='bold'
          sx={{ mt: 5, mb: 2 }}
        >
          Layanan Aftersales & Charging Station BSD untuk BYD M6
        </Typography>
        <Typography variant='body1' paragraph>
          Sebagai dealer resmi, <strong>BYD BSD Serpong</strong> siap memberikan
          layanan purna jual terbaik untuk armada BYD M6 Anda. Ekosistem{' '}
          <strong>Charging Station BSD</strong> yang semakin masif, baik di mal
          maupun area publik, memastikan Anda tidak perlu khawatir melakukan
          pengisian daya baterai Blade yang efisien. Kami juga siap membantu
          proses konsultasi Pajak Murah mobil listrik dan simulasi kredit ringan
          untuk keluarga Anda.
        </Typography>

        {/* Internal Linking */}
        <Paper
          variant='outlined'
          sx={{ p: 3, my: 5, backgroundColor: '#f9f9f9' }}
        >
          <Typography variant='h6' fontWeight='bold' gutterBottom>
            Artikel Menarik Lainnya:
          </Typography>
          <Typography variant='body2' sx={{ lineHeight: 2 }}>
            • Butuh SUV yang lebih sporty? Cek{' '}
            <Link
              component={RouterLink}
              to='/blog/sealion-7-suv-listrik-sporty'
            >
              Spesifikasi BYD Sealion 7
            </Link>
            .<br />• Ingin MPV Ultra-Luxury? Lihat{' '}
            <Link
              component={RouterLink}
              to='/blog/denza-d9-indonesia-mpv-mewah'
            >
              Kenyamanan Danza D9 Indonesia
            </Link>
            .<br />• Cek daftar harga lengkap di{' '}
            <Link component={RouterLink} to='/pricing'>
              Price List BYD 2026
            </Link>
            .
          </Typography>
        </Paper>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            backgroundColor: '#003d7a',
            color: '#fff',
            borderRadius: 2,
          }}
        >
          <Typography
            variant='h4'
            component='h2'
            fontWeight='bold'
            gutterBottom
          >
            Rasakan Kenyamanan BYD M6 Sekarang!
          </Typography>
          <Typography variant='body1' sx={{ mb: 4, px: 2 }}>
            Booking test drive di BSD Serpong dan dapatkan penawaran spesial
            serta hitungan Pajak Murah mobil listrik bersama Junius.
          </Typography>
          <Button
            variant='contained'
            size='large'
            href='https://wa.me/6281286864825'
            target='_blank'
            sx={{
              backgroundColor: '#25D366',
              color: '#fff',
              fontWeight: 'bold',
              px: 6,
              py: 1.5,
              borderRadius: '25px',
              '&:hover': { backgroundColor: '#128C7E' },
              fontSize: '1.1rem',
            }}
          >
            Konsultasi WhatsApp dengan Junius
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default M6Detail;
